import { useCallback, useMemo, useState } from 'react';

import { FeatureFlags } from '~src/commonInfra/featureFlags/FeatureFlags';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { ResultHelper } from '~src/httpClient/utils/ResultHelper';
import WebHttpClient from '~src/httpClient/WebHttpClient';
import { ActivateTrialRequest, IActivateTrialResult } from '~src/siteManagement/serverApi/requests/trial/ActivateTrialRequest';
import { ISiteTrial, TrialType } from '~src/siteManagement/serverApi/types/ISiteTrial';

import { useGetSiteTrials } from './useGetSiteTrials';

export const useGetSiteTrial = (trialType: TrialType): [ISiteTrial | undefined, () => Promise<boolean>] => {
  const [siteTrials] = useGetSiteTrials();
  const [activatedSiteTrial, setActivatedSiteTrial] = useState<ISiteTrial | undefined>(undefined);

  const siteTrial = useMemo(() => {
    return activatedSiteTrial ?? siteTrials?.find(t => t.trialType === trialType);
  }, [activatedSiteTrial, siteTrials, trialType]);

  const activateTrial = useCallback(async (): Promise<boolean> => {
    // A trial can be (re)activated when there is no existing trial, or when an
    // existing trial reports canRestart (expired with newly-onboarded features).
    if (!FeatureFlags.tabAITryItEnabled || (siteTrial && !siteTrial.canRestart)) {
      return false;
    }

    const result = await WebHttpClient.sendRequest(new ActivateTrialRequest({ trialType })) as IResult & IActivateTrialResult;
    if (ResultHelper.hasErrors(result)) {
      const err = result.errors![0];
      throw new Error(err.message ?? String(err.code));
    }
    setActivatedSiteTrial(result.trial);
    return true;
  }, [siteTrial, trialType]);

  return [siteTrial, activateTrial];
};
